import { DayStats } from '../../hooks/useHistoricalVibes'

interface HistoricalVibesChartProps {
  data: DayStats[]
}

export default function HistoricalVibesChart({ data }: HistoricalVibesChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">No historical data available yet.</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Data will appear as people vote over the coming days.
        </p>
      </div>
    )
  }

  // Check how many days actually have votes
  const daysWithVotes = data.filter(d => d.totalVotes > 0).length

  // Format date for display (e.g., "Nov 6")
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00Z')
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  // Get sentiment emoji
  const getSentimentEmoji = (sentiment: string) => {
    switch (sentiment) {
      case 'Bullish':
        return '📈'
      case 'Bearish':
        return '📉'
      default:
        return '➖'
    }
  }

  return (
    <div className="glass-card rounded-3xl p-6">
      <h3 className="text-sm font-bold mb-4 text-white text-center uppercase tracking-wider">
        Last 7 Days
      </h3>

      {/* Bar Chart */}
      <div className="space-y-3 mb-6">
        {data.map((day) => {
          const hasVotes = day.totalVotes > 0

          return (
            <div key={day.dateKey} className="space-y-1">
              <div className="flex justify-between items-center text-xs text-gray-400">
                <span className="font-medium font-mono">{formatDate(day.date)}</span>
                {hasVotes && (
                  <span className="flex items-center gap-1.5">
                    <span className="opacity-80">{getSentimentEmoji(day.sentiment)}</span>
                    <span className={`font-bold ${day.positivePercentage > 50 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {day.positivePercentage > 50 ? day.positivePercentage : 100 - day.positivePercentage}%
                    </span>
                  </span>
                )}
              </div>

              <div className="relative h-8 bg-white/5 rounded-xl overflow-hidden backdrop-blur-sm border border-white/5">
                {hasVotes ? (
                  <>
                    {/* Positive votes (Neon Green) */}
                    <div
                      className="absolute left-0 top-0 h-full bg-gradient-to-r from-emerald-500/80 to-teal-400/80 transition-all duration-500"
                      style={{ width: `${day.positivePercentage}%` }}
                    />
                    {/* Negative votes (Neon Red) */}
                    <div
                      className="absolute right-0 top-0 h-full bg-gradient-to-l from-rose-500/80 to-orange-400/80 transition-all duration-500"
                      style={{ width: `${100 - day.positivePercentage}%` }}
                    />

                    {/* Vote count label - centered with subtle shadow */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]">
                        {day.totalVotes} vote{day.totalVotes !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[10px] text-gray-500 font-medium">
                      No votes
                    </span>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-6 text-xs text-gray-400">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.5)]" />
          <span>Bullish</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-rose-400 rounded-full shadow-[0_0_8px_rgba(251,113,133,0.5)]" />
          <span>Bearish</span>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-6 pt-4 border-t border-white/10">
        <div className="text-center space-y-3">
          <p className="text-sm text-gray-300">
            Total votes: <span className="font-bold text-white text-lg ml-1">
              {data.reduce((sum, day) => sum + day.totalVotes, 0)}
            </span>
          </p>
          {daysWithVotes < 7 && (
            <p className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg px-3 py-2 inline-block">
              💡 Chart fills up as you vote daily. {7 - daysWithVotes} day{7 - daysWithVotes !== 1 ? 's' : ''} to go!
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

